import * as Yup from "yup";
import pricingValidation, {
  otherDefaultFeesPrettyNames,
} from "../../../components/modals/add-new-application-modal/pricing-validation";
import { calculateSalesTotal } from "./utils/validation";
import {
  isAchProcessorId,
  hasAchProcessor,
  hasCcProcessor,
  shouldSkipPricingMinimums,
} from "./utils/processor-pricing-rules";
import config from "../../../config";

const phoneRegex = /^\(?(\d{3})\)?[- ]?(\d{3})[- ]?(\d{4})$/;
const SSNRegex = /^\d{3}-\d{2}-\d{4}$/;

// Required only when is_multiple_locations === "yes", nullable otherwise.
const requiredWhenMultipleLocations = (message) =>
  Yup.string().when("is_multiple_locations", {
    is: "yes",
    then: (schema) => schema.required(message),
    otherwise: (schema) => schema.nullable(),
  });

// NOTE: Keeping this here for v2 create template page.
// This getDefaultFees function can be safely removed if v2 create template page dependency
// is taken away after integrating pricing defaults on it.
export const getDefaultFees = (organizationId) => {
  if (organizationId == config.paymentcloud.PAYCHEX_ORG_ID) {
    return pricingValidation.pricingValidation.paychex_default_fee;
  } else {
    return pricingValidation.pricingValidation.default_fee;
  }
};

export const businessInformationSchema = Yup.object().shape({
  type_of_ownership: Yup.string()
    .nullable()
    .required("* Type of Ownership is required"),
  legal_business_name: Yup.string()
    .nullable()
    .required("* Legal Business Name is required"),
  dba_name: Yup.string().nullable().required("* DBA Name is required"),
  tax_ein: Yup.string()
    .nullable()
    .required("* Tax ID is required")
    .matches(/^\d{2}-?\d{7}$|9\d{2}-?\d{2}-?\d{4}$/, "Enter a valid Tax ID"),
  premise_type: Yup.string().nullable().required("* Premise Type is required"),
  ownership_years: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value,
    )
    .nullable()
    .min(0, "Years cannot be negative")
    .test(
      "total-ownership",
      "At least 1 month of ownership is required",
      function (value) {
        const { ownership_months } = this.parent;
        return value > 0 || ownership_months > 0;
      },
    ),
  ownership_months: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value,
    )
    .nullable()
    .min(0, "Months cannot be negative")
    .max(11, "Months must be less than 12")
    .test(
      "total-ownership",
      "At least 1 month of ownership is required",
      function (value) {
        const { ownership_years } = this.parent;
        return value > 0 || ownership_years > 0;
      },
    ),
  business_type: Yup.string()
    .nullable()
    .required("* Business Type is required"),
  // Required only when a Payliance processor is selected (see paylianceIndustrySchema).
  payliance_industry: Yup.string().nullable(),
  tax_exempt: Yup.string()
    .oneOf(["yes", "no"], "Must be either 'yes' or 'no'")
    .nullable(),
  business_email: Yup.string()
    .nullable()
    .required("* Business Email is required"),
  business_phone: Yup.string()
    .nullable()
    .matches(phoneRegex, "* Enter a valid phone")
    .required("* Business Phone is required"),
  business_website: Yup.string()
    .nullable()
    .test("website-url", "* Must be valid URL", (value) => {
      if (!value || value === "") return true;
      return /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/.test(
        value,
      );
    })
    .when("premise_type", {
      is: (premise_type) => premise_type == 8,
      then: (schema) => schema.required("* Business Website is required"),
    }),
  dba_address: Yup.string().nullable().required("* DBA Address is required"),
  dba_city: Yup.string().nullable().required("* DBA City is required"),
  dba_state: Yup.string().nullable().required("* DBA State is required"),
  dba_zip: Yup.string().nullable().required("* DBA Zip is required"),
  location_years: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value,
    )
    .nullable()
    .min(0, "Years cannot be negative")
    .test(
      "total-location",
      "At least 1 month of location is required",
      function (value) {
        const { location_months } = this.parent;
        return value > 0 || location_months > 0;
      },
    ),
  location_months: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value,
    )
    .nullable()
    .min(0, "Months cannot be negative")
    .max(11, "Months must be less than 12")
    .test(
      "total-location",
      "At least 1 month of location is required",
      function (value) {
        const { location_years } = this.parent;
        return value > 0 || location_years > 0;
      },
    ),
  same_as_dba: Yup.string()
    .oneOf(["yes", "no"], "Must be either 'yes' or 'no'")
    .nullable(),
  legal_address: Yup.string()
    .nullable()
    .when("same_as_dba", {
      is: "no",
      then: Yup.string().nullable().required("* Legal Address is required"),
      otherwise: Yup.string().nullable(),
    }),
  legal_city: Yup.string()
    .nullable()
    .when("same_as_dba", {
      is: "no",
      then: Yup.string().nullable().required("* Legal City is required"),
      otherwise: Yup.string().nullable(),
    }),
  legal_state: Yup.string()
    .nullable()
    .when("same_as_dba", {
      is: "no",
      then: Yup.string().nullable().required("* Legal State is required"),
      otherwise: Yup.string().nullable(),
    }),
  legal_zip: Yup.string()
    .nullable()
    .when("same_as_dba", {
      is: "no",
      then: Yup.string().nullable().required("* Legal Zip is required"),
      otherwise: Yup.string().nullable(),
    }),
  legal_phone: Yup.string()
    .nullable()
    .when("same_as_dba", {
      is: "no",
      then: Yup.string()
        .nullable()
        .matches(phoneRegex, "* Enter a valid phone")
        .required("* Legal Phone is required"),
      otherwise: Yup.string().nullable(),
    }),
});

// Concatenated onto the business information / full form schema only when the
// selected override bank includes a Payliance processor.
// TODO: make the field required when override_bank issue is fixed
export const paylianceIndustrySchema = Yup.object().shape({
  payliance_industry: Yup.string().nullable(),
});

export const contactInfoSchema = Yup.object().shape({
  first_name: Yup.string()
    .matches(/^[\p{L}. ]+$/u, "Only letters, periods, and accents are allowed")
    .trim()
    .required("* First Name is required"),
  last_name: Yup.string()
    .matches(/^[\p{L}. ]+$/u, "Only letters, periods, and accents are allowed")
    .trim()
    .required("* Last Name is required"),
  dba_name: Yup.string().trim().required("* DBA Name is required"),
  dba_street: Yup.string().trim().required("* DBA Address is required"),
  dba_city: Yup.string().trim().required("* DBA City is required"),
  dba_state: Yup.string().trim().required("* DBA State is required"),
  country: Yup.string().required("* Country is required"),
  dba_zip: Yup.string().when("country", {
    is: "en",
    then: Yup.string()
      .trim()
      .matches(/^\d{5}(-\d{4})?$/, "* Invalid Zip Code")
      .required("* DBA Zip is required"),
    otherwise: Yup.string().nullable(),
  }),
  postal_code: Yup.string().when("country", {
    is: "ca",
    then: Yup.string()
      .nullable()
      .required("* DBA Postal Code is required")
      .matches(
        /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/,
        "*Enter a valid Canadian Postal Code",
      ),
    otherwise: Yup.string().nullable(),
  }),
  email: Yup.string()
    .trim()
    .email("* Invalid Email")
    .required("* Business Email is required"),
  mobile: Yup.string()
    .matches(phoneRegex, "* Invalid Phone Number")
    .required("* Business Phone is required"),
  is_merchant_referral: Yup.string()
    .oneOf(["yes", "no"], "Invalid selection")
    .required("* Merchant Referral selection is required"),
  referrer_name: Yup.string().when("is_merchant_referral", {
    is: "yes",
    then: Yup.string().required("* Referrer Business Name is required"),
    otherwise: Yup.string().notRequired(),
  }),
  referrer_mid: Yup.string().when("is_merchant_referral", {
    is: "yes",
    then: Yup.string().required("* Referrer MID is required"),
    otherwise: Yup.string().notRequired(),
  }),
  is_tm: Yup.string()
    .oneOf(["yes", "no"], "Invalid selection")
    .required("* TM Lead selection is required"),
  telemarketer_name: Yup.string().when("is_tm", {
    is: "yes",
    then: Yup.string().required("* Telemarketer's name is required"),
    otherwise: Yup.string().notRequired(),
  }),
});

const reprogrammedHardwareSchema = Yup.object().shape({
  equipment_type: Yup.string()
    .oneOf(["gateway"], "* Invalid equipment type")
    .required("* Equipment type is required"),
  equipment_model: Yup.string()
    .oneOf(["gateway"], "* Invalid equipment model")
    .required("* Equipment model is required"),
  serial_number: Yup.string().required("* Serial number is required"),
  quantity: Yup.number()
    .integer("* Quantity must be an integer")
    .min(1, "* Quantity must be at least 1")
    .required("* Quantity is required"),
  application_type: Yup.string()
    .oneOf(["gateway"], "* Invalid application type")
    .required("* Application type is required"),
  comm_method: Yup.string()
    .oneOf(["ip"], "* Invalid communication method")
    .required("* Communication method is required"),
  auto_batch: Yup.string()
    .oneOf(["yes", "no"], "* Invalid auto batch value")
    .required("* Auto batch is required"),
  auto_batch_time: Yup.string()
    .matches(/^\d{2}:\d{2}$/, "* Invalid time format (HH:MM)")
    .required("* Auto batch time is required"),
  special_instructions: Yup.string(),
  uw_notes: Yup.string(),
});

export const equipmentSchema = {
  equipments: Yup.array().when("var_only", {
    is: (var_only) => !var_only || Object.keys(var_only).length === 0,
    then: Yup.array().min(1, "Add at least 1 equipment"),
    otherwise: Yup.array(),
  }),
};

const getAllDefaultFeeRules = (pricingDefaults) => {
  return Object.keys(otherDefaultFeesPrettyNames).reduce((acc, key) => {
    acc[key] = Yup.lazy((value, context) => {
      let schema = Yup.number()
        .transform((value, originalValue) => (originalValue === "" ? 0 : value))
        .required(`${otherDefaultFeesPrettyNames[key]} is required`);

      const pricingModel = context.parent.pricing_model;
      const fee =
        pricingDefaults?.[pricingModel]?.[key] ??
        pricingDefaults?.default_fees?.[key];

      if (typeof fee?.minimum === "number") {
        schema = schema.min(
          fee.minimum,
          `Please enter a fee of $${fee.minimum.toFixed(2)} or higher.`,
        );
      }

      if (typeof fee?.maximum === "number") {
        schema = schema.max(
          fee.maximum,
          `Please enter a fee of $${fee.maximum.toFixed(2)} or lower.`,
        );
      }

      return schema;
    });

    return acc;
  }, {});
};

const paylianceIndustryPricingField = {
  payliance_industry: Yup.string()
    .nullable()
    .when("processors", {
      is: (processors) => hasAchProcessor(processors),
      then: (schema) =>
        schema.required("* Payliance Industry is required"),
      otherwise: (schema) => schema.nullable(),
    }),
};

const stripMinimums = (val) => {
  if (Array.isArray(val)) return val.map(stripMinimums);
  if (val && typeof val === "object") {
    return Object.fromEntries(
      Object.entries(val).map(([k, v]) =>
        k === "minimum" && typeof v === "number"
          ? [k, 0]
          : [k, stripMinimums(v)],
      ),
    );
  }
  return val;
};

const buildPricingInfoSchema = (
  pricingDefaults,
  { requirePaylianceIndustry = false } = {},
) =>
  Yup.object().shape({
    pricing_model: Yup.string()
      .trim()
      .required("* Pricing Model is required")
      .test(
        "cc-processor-needs-cc-pricing",
        "Credit card pricing is required when a credit card processor is selected",
        function (value) {
          return (
            !hasCcProcessor(this.parent?.processors) ||
            Boolean(value && value !== "ach")
          );
        },
      )
      .test(
        "ach-processor-needs-ach-pricing",
        "ACH pricing is required when an ACH processor is selected",
        function (value) {
          return (
            !hasAchProcessor(this.parent?.processors) ||
            value === "ach" ||
            this.parent?.add_ach === "yes"
          );
        },
      ),
    pricing_approved_by_rate_desk: Yup.string().oneOf(
      ["yes", "no"],
      "Must be either 'yes' or 'no'",
    ),
    // Dual Pricing
    dual_pricing_rate: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when("pricing_model", {
        is: "dual_pricing",
        then: (schema) =>
          schema
            .required("Dual Pricing Rate is required")
            .min(
              pricingDefaults?.dual_pricing?.dual_pricing_rate?.minimum,
              `Please enter a rate of ${pricingDefaults?.dual_pricing?.dual_pricing_rate?.minimum}% or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
    dual_pricing_pin_debit_network_fee: Yup.string()
      .oneOf(["yes", "no"], "Must be either 'yes' or 'no'")
      .when("pricing_model", {
        is: "dual_pricing",
        then: (schema) => schema.required("PIN Debit Network Fee is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    dual_pricing_per_transaction: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when("pricing_model", {
        is: "dual_pricing",
        then: (schema) =>
          schema
            .required("Transaction fee is required")
            .min(
              pricingDefaults?.dual_pricing?.dual_pricing_per_transaction
                ?.minimum,
              `Please enter a fee of $${pricingDefaults?.dual_pricing?.dual_pricing_per_transaction?.minimum} or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
    dual_pricing_monthly_fee: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when("pricing_model", {
        is: "dual_pricing",
        then: (schema) =>
          schema
            .min(
              pricingDefaults?.dual_pricing?.dual_pricing_monthly_fee?.minimum,
              `Please enter a fee of $${pricingDefaults?.dual_pricing?.dual_pricing_monthly_fee?.minimum} or higher.`,
            )
            .required("Dual Pricing Monthly Fee is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    // surcharge
    surcharge_rate: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when("pricing_model", {
        is: "surcharge",
        then: (schema) =>
          schema
            .required("Surcharge Rate is required")
            .min(
              pricingDefaults?.surcharge?.surcharge_rate?.minimum,
              `Please enter a rate of ${pricingDefaults?.surcharge?.surcharge_rate.minimum}% or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
    surcharge_per_transaction: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when("pricing_model", {
        is: "surcharge",
        then: (schema) =>
          schema
            .required("Per Transaction fee is required")
            .min(
              pricingDefaults?.surcharge?.surcharge_per_transaction.minimum,
              `Please enter a fee of $${pricingDefaults?.surcharge?.surcharge_per_transaction.minimum} or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    surcharge_monthly_fee: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when("pricing_model", {
        is: "surcharge",
        then: (schema) =>
          schema
            .required("Monthly Fee is required")
            .min(
              pricingDefaults?.surcharge?.surcharge_monthly_fee.minimum,
              `Please enter a fee of $${pricingDefaults?.surcharge?.surcharge_monthly_fee.minimum} or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
    surcharge_signature_debit_rate: Yup.number()
      .required("Signature Debit Rate is required")
      .when("tiered_amex_structure", {
        is: "ic_plus",
        then: (schema) =>
          schema
            .required("Signature Debit Rate is required")
            .min(
              pricingDefaults?.surcharge?.surcharge_signature_debit_rate.minimum,
              `Please enter a rate of ${pricingDefaults?.surcharge?.surcharge_signature_debit_rate.minimum}% or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
    surcharge_signature_debit_per_transaction: Yup.number().when(
      "tiered_amex_structure",
      {
        is: "ic_plus",
        then: (schema) =>
          schema
            .required("Signature Debit Per Txn fee is required")
            .min(
              pricingDefaults?.surcharge
                ?.surcharge_signature_debit_per_transaction.minimum,
              `Please enter a fee of $${pricingDefaults?.surcharge?.surcharge_signature_debit_per_transaction.minimum} or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      },
    ),

    surcharge_qual_debit_rate: Yup.number().when("tiered_amex_structure", {
      is: "tiered",
      then: (schema) =>
        schema
          .required("Qualified Debit Rate is required")
          .min(
            pricingDefaults?.surcharge?.surcharge_qual_debit_rate.minimum,
            `Please enter a rate of ${pricingDefaults?.surcharge?.surcharge_qual_debit_rate.minimum}% or higher.`,
          ),
      otherwise: (schema) => schema.notRequired(),
    }),

    surcharge_qual_debit_per_transaction: Yup.number().when(
      "tiered_amex_structure",
      {
        is: "tiered",
        then: (schema) =>
          schema
            .required("Qualified Debit Per Transaction fee is required")
            .min(
              pricingDefaults?.surcharge?.surcharge_qual_debit_per_transaction
                .minimum,
              `Please enter a fee of $${pricingDefaults?.surcharge?.surcharge_qual_debit_per_transaction.minimum} or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      },
    ),

    surcharge_mid_qual_debit_rate: Yup.number().when("tiered_amex_structure", {
      is: "tiered",
      then: (schema) =>
        schema
          .required("Mid-Qualified Debit Rate is required")
          .min(
            pricingDefaults?.surcharge?.surcharge_mid_qual_debit_rate.minimum,
            `Please enter a rate of ${pricingDefaults?.surcharge?.surcharge_mid_qual_debit_rate.minimum}% or higher.`,
          ),
      otherwise: (schema) => schema.notRequired(),
    }),

    surcharge_mid_qual_debit_per_transaction: Yup.number().when(
      "tiered_amex_structure",
      {
        is: "tiered",
        then: (schema) =>
          schema
            .required("Mid-Qualified Debit Per Transaction fee is required")
            .min(
              pricingDefaults?.surcharge?.surcharge_mid_qual_debit_per_transaction
                .minimum,
              `Please enter a fee of $${pricingDefaults?.surcharge?.surcharge_mid_qual_debit_per_transaction.minimum} or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      },
    ),

    surcharge_non_qual_debit_rate: Yup.number().when("tiered_amex_structure", {
      is: "tiered",
      then: (schema) =>
        schema
          .required("Non-Qualified Debit Rate is required")
          .min(
            pricingDefaults?.surcharge?.surcharge_non_qual_debit_rate.minimum,
            `Please enter a rate of ${pricingDefaults?.surcharge?.surcharge_non_qual_debit_rate.minimum}% or higher.`,
          ),
      otherwise: (schema) => schema.notRequired(),
    }),

    surcharge_non_qual_debit_per_transaction: Yup.number().when(
      "tiered_amex_structure",
      {
        is: "tiered",
        then: (schema) =>
          schema
            .required("Non-Qualified Debit Per Transaction fee is required")
            .min(
              pricingDefaults?.surcharge?.surcharge_non_qual_debit_per_transaction
                .minimum,
              `Please enter a fee of $${pricingDefaults?.surcharge?.surcharge_non_qual_debit_per_transaction.minimum} or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      },
    ),

    //Flat Rate
    flat_rate: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when("pricing_model", {
        is: "flat_rate",
        then: (schema) =>
          schema
            .required("Flat Rate is required")
            .min(
              pricingDefaults?.flat_rate?.flat_rate.minimum,
              `Please enter a rate of ${pricingDefaults?.flat_rate?.flat_rate.minimum}% or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    flat_rate_transaction: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when("pricing_model", {
        is: "flat_rate",
        then: (schema) =>
          schema
            .required("Txn Fee is required")
            .min(
              pricingDefaults?.flat_rate?.flat_rate_transaction.minimum,
              `Please enter a fee of $${pricingDefaults?.flat_rate?.flat_rate_transaction.minimum} or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    flat_rate_amex: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when("pricing_model", {
        is: "flat_rate",
        then: (schema) =>
          schema
            .required("AMEX Flat Rate is required")
            .min(
              pricingDefaults?.flat_rate?.flat_rate_amex.minimum,
              `Please enter a rate of ${pricingDefaults?.flat_rate?.flat_rate_amex.minimum}% or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    flat_rate_amex_transaction: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when("pricing_model", {
        is: "flat_rate",
        then: (schema) =>
          schema
            .required("AMEX Txn Fee is required")
            .min(
              pricingDefaults?.flat_rate?.flat_rate_amex_transaction.minimum,
              `Please enter a fee of $${pricingDefaults?.flat_rate?.flat_rate_amex_transaction.minimum} or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    // IC+
    ic_plus_rate: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when("pricing_model", {
        is: "ic_plus",
        then: (schema) =>
          schema
            .required("IC+ Rate is required")
            .min(
              pricingDefaults?.ic_plus?.ic_plus_rate.minimum,
              `Please enter a rate of ${pricingDefaults?.ic_plus?.ic_plus_rate.minimum}% or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    ic_plus_transaction_fee: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when("pricing_model", {
        is: "ic_plus",
        then: (schema) =>
          schema
            .required("Txn Fee is required")
            .min(
              pricingDefaults?.ic_plus?.ic_plus_transaction_fee.minimum,
              `Please enter a fee of $${pricingDefaults?.ic_plus?.ic_plus_transaction_fee.minimum} or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    ic_plus_amex_rate: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when("pricing_model", {
        is: "ic_plus",
        then: (schema) =>
          schema
            .required("AMEX IC+ Rate is required")
            .min(
              pricingDefaults?.ic_plus?.ic_plus_amex_rate.minimum,
              `Please enter a rate of ${pricingDefaults?.ic_plus?.ic_plus_amex_rate.minimum}% or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    ic_plus_amex_transaction_fee: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when("pricing_model", {
        is: "ic_plus",
        then: (schema) =>
          schema
            .required("AMEX Txn Fee is required")
            .min(
              pricingDefaults?.ic_plus?.ic_plus_amex_transaction_fee.minimum,
              `Please enter a fee of $${pricingDefaults?.ic_plus?.ic_plus_amex_transaction_fee.minimum} or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    // Tiered
    // Qualified Fields
    tiered_qual_rate: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when("pricing_model", {
        is: "tiered",
        then: (schema) =>
          schema
            .required("Qualified Rate is required")
            .min(
              pricingDefaults?.tiered?.tiered_qual_rate.minimum,
              `Please enter a rate of ${pricingDefaults?.tiered?.tiered_qual_rate.minimum}% or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    tiered_qual_transaction: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when("pricing_model", {
        is: "tiered",
        then: (schema) =>
          schema
            .required("Qualified Txn Fee is required")
            .min(
              pricingDefaults?.tiered?.tiered_qual_transaction.minimum,
              `Please enter a fee of $${pricingDefaults?.tiered?.tiered_qual_transaction.minimum} or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    tiered_qual_debit_rate: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when("pricing_model", {
        is: "tiered",
        then: (schema) =>
          schema
            .required("Qualified Debit Rate is required")
            .min(
              pricingDefaults?.tiered?.tiered_qual_debit_rate.minimum,
              `Please enter a rate of ${pricingDefaults?.tiered?.tiered_qual_debit_rate.minimum}% or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    tiered_qual_debit_per_transaction: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when("pricing_model", {
        is: "tiered",
        then: (schema) =>
          schema
            .required("Qualified Debit Per Txn fee is required")
            .min(
              pricingDefaults?.tiered?.tiered_qual_debit_per_transaction
                .minimum,
              `Please enter a fee of $${pricingDefaults?.tiered?.tiered_qual_debit_per_transaction.minimum} or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    // Mid-Qualified Fields
    tiered_mid_qual_rate: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .when("pricing_model", {
        is: "tiered",
        then: (schema) =>
          schema
            .notRequired()
            .min(
              pricingDefaults?.tiered?.tiered_mid_qual_rate.minimum,
              `Please enter a rate of ${pricingDefaults?.tiered?.tiered_mid_qual_rate.minimum}% or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    tiered_mid_qual_transaction: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .when("pricing_model", {
        is: "tiered",
        then: (schema) =>
          schema
            .notRequired()
            .min(
              pricingDefaults?.tiered?.tiered_mid_qual_transaction.minimum,
              `Please enter a fee of $${pricingDefaults?.tiered?.tiered_mid_qual_transaction.minimum} or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    tiered_mid_qual_debit_rate: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .when("pricing_model", {
        is: "tiered",
        then: (schema) =>
          schema
            .notRequired()
            .min(
              pricingDefaults?.tiered?.tiered_mid_qual_debit_rate.minimum,
              `Please enter a rate of ${pricingDefaults?.tiered?.tiered_mid_qual_debit_rate.minimum}% or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    tiered_mid_qual_debit_per_transaction: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .when("pricing_model", {
        is: "tiered",
        then: (schema) =>
          schema
            .notRequired()
            .min(
              pricingDefaults?.tiered?.tiered_mid_qual_debit_per_transaction
                .minimum,
              `Please enter a fee of $${pricingDefaults?.tiered?.tiered_mid_qual_debit_per_transaction.minimum} or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    // Non-Qualified Fields
    tiered_non_qual_rate: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .when("pricing_model", {
        is: "tiered",
        then: (schema) =>
          schema
            .notRequired()
            .min(
              pricingDefaults?.tiered?.tiered_non_qual_rate.minimum,
              `Please enter a rate of ${pricingDefaults?.tiered?.tiered_non_qual_rate.minimum}% or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    tiered_non_qual_transaction: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .when("pricing_model", {
        is: "tiered",
        then: (schema) =>
          schema
            .notRequired()
            .min(
              pricingDefaults?.tiered?.tiered_non_qual_transaction.minimum,
              `Please enter a fee of $${pricingDefaults?.tiered?.tiered_non_qual_transaction.minimum} or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    tiered_non_qual_debit_rate: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .when("pricing_model", {
        is: "tiered",
        then: (schema) =>
          schema
            .notRequired()
            .min(
              pricingDefaults?.tiered?.tiered_non_qual_debit_rate.minimum,
              `Please enter a rate of ${pricingDefaults?.tiered?.tiered_non_qual_debit_rate.minimum}% or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    tiered_non_qual_debit_per_transaction: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .when("pricing_model", {
        is: "tiered",
        then: (schema) =>
          schema
            .notRequired()
            .min(
              pricingDefaults?.tiered?.tiered_non_qual_debit_per_transaction
                .minimum,
              `Please enter a fee of $${pricingDefaults?.tiered?.tiered_non_qual_debit_per_transaction.minimum} or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    // Tiered Rate fields
    // AMEX Tier 1
    amex_tier_one_rate: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .when(["tiered_amex_structure", "pricing_model"], (tiered_amex_structure, pricing_model, schema) => {
        if (tiered_amex_structure !== "tiered" || pricing_model !== "tiered") {
          return schema.notRequired();
        }

        return schema
          .required("AMEX Tier 1 Rate is required")
          .min(
            pricingDefaults?.tiered?.amex_tier_one_rate.minimum,
            `Please enter a rate of ${pricingDefaults?.tiered?.amex_tier_one_rate.minimum}% or higher.`,
          );
      }),
    amex_tier_one_fee: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .when(["tiered_amex_structure", "pricing_model"], (tiered_amex_structure, pricing_model, schema) => {
        if (tiered_amex_structure !== "tiered" || pricing_model !== "tiered") {
          return schema.notRequired();
        }

        return schema
          .required("AMEX Tier 1 Txn Fee is required")
          .min(
            pricingDefaults?.tiered?.amex_tier_one_fee.minimum,
            `Please enter a fee of $${pricingDefaults?.tiered?.amex_tier_one_fee.minimum} or higher.`,
          );
      }),

    // AMEX Tier 2
    amex_tier_two_rate: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .when("tiered_amex_structure", {
        is: "tiered",
        then: (schema) =>
          schema
            .notRequired()
            .min(
              pricingDefaults?.tiered?.amex_tier_two_rate.minimum,
              `Please enter a rate of ${pricingDefaults?.tiered?.amex_tier_two_rate.minimum}% or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
    amex_tier_two_fee: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .when("tiered_amex_structure", {
        is: "tiered",
        then: (schema) =>
          schema
            .notRequired()
            .min(
              pricingDefaults?.tiered?.amex_tier_two_fee.minimum,
              `Please enter a fee of $${pricingDefaults?.tiered?.amex_tier_two_fee.minimum} or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    // AMEX Tier 3
    amex_tier_three_rate: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .when("tiered_amex_structure", {
        is: "tiered",
        then: (schema) =>
          schema
            .notRequired()
            .min(
              pricingDefaults?.tiered?.amex_tier_three_rate.minimum,
              `Please enter a rate of ${pricingDefaults?.tiered?.amex_tier_three_rate.minimum}% or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
    amex_tier_three_fee: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .when("tiered_amex_structure", {
        is: "tiered",
        then: (schema) =>
          schema
            .notRequired()
            .min(
              pricingDefaults?.tiered?.amex_tier_three_fee.minimum,
              `Please enter a fee of $${pricingDefaults?.tiered?.amex_tier_three_fee.minimum} or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    // AMEX Tier 4
    amex_tier_four_rate: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .when("tiered_amex_structure", {
        is: "tiered",
        then: (schema) =>
          schema
            .notRequired()
            .min(
              pricingDefaults?.tiered?.amex_tier_four_rate.minimum,
              `Please enter a rate of ${pricingDefaults?.tiered?.amex_tier_four_rate.minimum}% or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
    amex_tier_four_fee: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .when("tiered_amex_structure", {
        is: "tiered",
        then: (schema) =>
          schema
            .notRequired()
            .min(
              pricingDefaults?.tiered?.amex_tier_four_fee.minimum,
              `Please enter a fee of $${pricingDefaults?.tiered?.amex_tier_four_fee.minimum} or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    // AMEX Flat Rate
    tiered_amex_flat_rate: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when("tiered_amex_structure", {
        is: "flat_rate",
        then: Yup.number()
          .required("AMEX Flat Rate is required")
          .min(
            pricingDefaults?.tiered?.tiered_amex_flat_rate.minimum,
            `Please enter a rate of ${pricingDefaults?.tiered?.tiered_amex_flat_rate.minimum}% or higher.`,
          ),
        otherwise: Yup.number(),
      }),

    tiered_amex_flat_transaction: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when("tiered_amex_structure", {
        is: "flat_rate",
        then: Yup.number()
          .required("AMEX Flat Transaction Fee is required")
          .min(
            pricingDefaults?.tiered?.tiered_amex_flat_transaction.minimum,
            `Please enter a fee of $${pricingDefaults?.tiered?.tiered_amex_flat_transaction.minimum} or higher.`,
          ),
        otherwise: Yup.number(),
      }),

    // AMEX Interchange Plus
    tiered_amex_ic_plus_rate: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when("tiered_amex_structure", {
        is: "ic_plus",
        then: Yup.number()
          .required("AMEX IC+ Rate is required")
          .min(
            pricingDefaults?.tiered?.tiered_amex_ic_plus_rate.minimum,
            `Please enter a rate of ${pricingDefaults?.tiered?.tiered_amex_ic_plus_rate.minimum}% or higher.`,
          ),
        otherwise: Yup.number(),
      }),

    tiered_amex_ic_plus_transaction: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when("tiered_amex_structure", {
        is: "ic_plus",
        then: Yup.number()
          .required("AMEX IC+ Transaction Fee is required")
          .min(
            pricingDefaults?.tiered?.tiered_amex_ic_plus_transaction.minimum,
            `Please enter a fee of $${pricingDefaults?.tiered?.tiered_amex_ic_plus_transaction.minimum} or higher.`,
          ),
        otherwise: Yup.number(),
      }),

    // ACH pricing
    add_ach: Yup.string().oneOf(["yes", "no"]),
    ach_discount_rate: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when(["pricing_model", "add_ach"], (pricing_model, add_ach, schema) => {
        if (pricing_model !== "ach" && add_ach !== "yes") {
          return schema.notRequired();
        }

        return schema
          .min(
            pricingDefaults?.ach?.ach_discount_rate?.minimum ?? 0,
            `Please enter a rate of ${pricingDefaults?.ach?.ach_discount_rate?.minimum}% or higher.`,
          )
          .typeError("Enter a valid number");
      }),

    ach_transaction_fee_per_item: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when(["pricing_model", "add_ach"], (pricing_model, add_ach, schema) => {
        if (pricing_model !== "ach" && add_ach !== "yes") {
          return schema.notRequired();
        }

        return schema
          .required("Transaction Fee Per Item is required")
          .min(
            pricingDefaults?.ach?.ach_transaction_fee_per_item?.minimum ?? 0,
            `Please enter a fee of $${pricingDefaults?.ach?.ach_transaction_fee_per_item.minimum} or higher.`,
          )
          .typeError("Enter a valid number");
      }),

    ach_statement_fee: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when(["pricing_model", "add_ach"], (pricing_model, add_ach, schema) => {
        if (pricing_model !== "ach" && add_ach !== "yes") {
          return schema.notRequired();
        }

        return schema
          .min(
            pricingDefaults?.ach?.ach_statement_fee.minimum,
            `Please enter a fee of $${pricingDefaults?.ach?.ach_statement_fee.minimum} or higher.`,
          )
          .typeError("Enter a valid number");
      }),

    ach_monthly_minimum: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when(["pricing_model", "add_ach"], (pricing_model, add_ach, schema) => {
        if (pricing_model !== "ach" && add_ach !== "yes") {
          return schema.notRequired();
        }

        return schema
          .min(
            pricingDefaults?.ach?.ach_monthly_minimum.minimum,
            `Please enter a minimum of $${pricingDefaults?.ach?.ach_monthly_minimum.minimum} or higher.`,
          )
          .typeError("Please enter a valid minimum");
      }),

    ach_return_fee: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when(["pricing_model", "add_ach"], (pricing_model, add_ach, schema) => {
        if (pricing_model !== "ach" && add_ach !== "yes") {
          return schema.notRequired();
        }

        return schema
          .min(
            pricingDefaults?.ach?.ach_return_fee.minimum,
            `Please enter a fee of $${pricingDefaults?.ach?.ach_return_fee.minimum} or higher.`,
          )
          .typeError("Enter a valid number");
      }),

    ach_noc_fee: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when(["pricing_model", "add_ach"], (pricing_model, add_ach, schema) => {
        if (pricing_model !== "ach" && add_ach !== "yes") {
          return schema.notRequired();
        }

        return schema
          .min(
            pricingDefaults?.ach?.ach_noc_fee.minimum,
            `Please enter a fee of $${pricingDefaults?.ach?.ach_noc_fee.minimum} or higher.`,
          )
          .typeError("Enter a valid number");
      }),

    ach_unauthorized_return_fee: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when(["pricing_model", "add_ach"], (pricing_model, add_ach, schema) => {
        if (pricing_model !== "ach" && add_ach !== "yes") {
          return schema.notRequired();
        }

        return schema
          .min(
            pricingDefaults?.ach?.ach_unauthorized_return_fee.minimum,
            `Please enter a fee of $${pricingDefaults?.ach?.ach_unauthorized_return_fee.minimum} or higher.`,
          )
          .typeError("Enter a valid number");
      }),

    ach_same_day_fee: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when(["pricing_model", "add_ach"], (pricing_model, add_ach, schema) => {
        if (pricing_model !== "ach" && add_ach !== "yes") {
          return schema.notRequired();
        }

        return schema
          .min(
            pricingDefaults?.ach?.ach_same_day_fee.minimum,
            `Please enter a fee of $${pricingDefaults?.ach?.ach_same_day_fee.minimum} or higher.`,
          )
          .typeError("Enter a valid number");
      }),

    ach_bank_validation_fee: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when(["pricing_model", "add_ach"], (pricing_model, add_ach, schema) => {
        if (pricing_model !== "ach" && add_ach !== "yes") {
          return schema.notRequired();
        }

        return schema
          .min(
            pricingDefaults?.ach?.ach_bank_validation_fee.minimum,
            `Please enter a fee of $${pricingDefaults?.ach?.ach_bank_validation_fee.minimum} or higher.`,
          )
          .typeError("Enter a valid number");
      }),

    ach_by_phone: Yup.string().oneOf(["yes", "no"]),
    ach_by_web: Yup.string().oneOf(["yes", "no"]),
    ach_by_preauth: Yup.string().oneOf(["yes", "no"]),
    ach_credit_transaction: Yup.string().oneOf(["yes", "no"]),

    // Note: This is just a placeholder/dummy field
    // so that we can validate the ACH capabilities.
    ach_capabilities: Yup.string().test(
      "ach-capabilities",
      "At least one ACH capability must be enabled",
      function (value) {
        const {
          add_ach,
          pricing_model,
          ach_by_phone,
          ach_by_web,
          ach_by_preauth,
          ach_credit_transaction,
        } = this.parent;

        if (add_ach !== "yes" && pricing_model !== "ach") {
          return true;
        }

        return (
          ach_by_phone === "yes" ||
          ach_by_web === "yes" ||
          ach_by_preauth === "yes" ||
          ach_credit_transaction === "yes"
        );
      },
    ),
    // Separate PIN debit fields for each pricing model
    // Surcharge PIN debit fields
    surcharge_pin_debit_network_fee: Yup.string()
      .oneOf(["yes", "no"], "Must be either 'yes' or 'no'")
      .when("pricing_model", {
        is: "surcharge",
        then: (schema) => schema.required("PIN Debit Network Fee is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

    surcharge_pin_debit_rate: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when(["pricing_model", "surcharge_pin_debit_network_fee"], (pricing_model, surcharge_pin_debit_network_fee, schema) => {
        if (pricing_model !== "surcharge" || surcharge_pin_debit_network_fee !== "yes") return schema.notRequired();

        return schema
          .required("PIN-based Debit Rate is required")
          .min(
            pricingDefaults?.surcharge?.surcharge_pin_debit_rate?.minimum,
            `Please enter a rate of ${pricingDefaults?.surcharge?.surcharge_pin_debit_rate?.minimum}% or higher.`,
          );
      }),

    surcharge_pin_debit_per_transaction: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when(["pricing_model", "surcharge_pin_debit_network_fee"], (pricing_model, surcharge_pin_debit_network_fee, schema) => {
        if (pricing_model !== "surcharge" || surcharge_pin_debit_network_fee !== "yes") return schema.notRequired();

        return schema
          .required("PIN-based Debit Per Txn fee is required")
          .min(
            pricingDefaults?.surcharge?.surcharge_pin_debit_per_transaction
              ?.minimum,
            `Please enter a fee of $${pricingDefaults?.surcharge?.surcharge_pin_debit_per_transaction?.minimum} or higher.`,
          );
      }),

    // Flat Rate PIN debit fields
    flat_rate_pin_debit_network_fee: Yup.string()
      .oneOf(["yes", "no"], "Must be either 'yes' or 'no'")
      .when("pricing_model", {
        is: "flat_rate",
        then: (schema) => schema.required("PIN Debit Network Fee is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

    flat_rate_pin_debit_rate: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when(["pricing_model", "flat_rate_pin_debit_network_fee"], (pricing_model, flat_rate_pin_debit_network_fee, schema) => {
        if (pricing_model !== "flat_rate" || flat_rate_pin_debit_network_fee !== "yes") return schema.notRequired();

        return schema
          .required("PIN-based Debit Rate is required")
          .min(
            pricingDefaults?.flat_rate?.flat_rate_pin_debit_rate?.minimum,
            `Please enter a rate of ${pricingDefaults?.flat_rate?.flat_rate_pin_debit_rate?.minimum}% or higher.`,
          );
      }),

    flat_rate_pin_debit_per_transaction: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when(["pricing_model", "flat_rate_pin_debit_network_fee"], (pricing_model, flat_rate_pin_debit_network_fee, schema) => {
        if (pricing_model !== "flat_rate" || flat_rate_pin_debit_network_fee !== "yes") return schema.notRequired();

        return schema
          .required("PIN-based Debit Per Txn fee is required")
          .min(
            pricingDefaults?.flat_rate?.flat_rate_pin_debit_per_transaction
              ?.minimum,
            `Please enter a fee of $${pricingDefaults?.flat_rate?.flat_rate_pin_debit_per_transaction?.minimum} or higher.`,
          );
      }),

    // IC+ PIN debit fields
    ic_plus_pin_debit_network_fee: Yup.string()
      .oneOf(["yes", "no"], "Must be either 'yes' or 'no'")
      .when("pricing_model", {
        is: "ic_plus",
        then: (schema) => schema.required("PIN Debit Network Fee is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

    ic_plus_pin_debit_rate: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when(["pricing_model", "ic_plus_pin_debit_network_fee"], (pricing_model, ic_plus_pin_debit_network_fee, schema) => {
        if (pricing_model !== "ic_plus" || ic_plus_pin_debit_network_fee !== "yes") return schema.notRequired();

        return schema
          .required("PIN-based Debit Rate is required")
          .min(
            pricingDefaults?.ic_plus?.ic_plus_pin_debit_rate?.minimum,
            `Please enter a rate of ${pricingDefaults?.ic_plus?.ic_plus_pin_debit_rate?.minimum}% or higher.`,
          );
      }),

    ic_plus_pin_debit_per_transaction: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when(["pricing_model", "ic_plus_pin_debit_network_fee"], (pricing_model, ic_plus_pin_debit_network_fee, schema) => {
        if (pricing_model !== "ic_plus" || ic_plus_pin_debit_network_fee !== "yes") return schema.notRequired();

        return schema
          .required("PIN-based Debit Per Txn fee is required")
          .min(
            pricingDefaults?.ic_plus?.ic_plus_pin_debit_per_transaction
              ?.minimum,
            `Please enter a fee of $${pricingDefaults?.ic_plus?.ic_plus_pin_debit_per_transaction?.minimum} or higher.`,
          );
      }),

    // Tiered PIN debit fields
    tiered_pin_debit_network_fee: Yup.string()
      .oneOf(["yes", "no"], "Must be either 'yes' or 'no'")
      .when("pricing_model", {
        is: "tiered",
        then: (schema) => schema.required("PIN Debit Network Fee is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

    tiered_pin_debit_rate: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when(["pricing_model", "tiered_pin_debit_network_fee"], (pricing_model, tiered_pin_debit_network_fee, schema) => {
        if (pricing_model !== "tiered" || tiered_pin_debit_network_fee !== "yes") return schema.notRequired();

        return schema
          .required("PIN-based Debit Rate is required")
          .min(
            pricingDefaults?.tiered?.tiered_pin_debit_rate?.minimum,
            `Please enter a rate of ${pricingDefaults?.tiered?.tiered_pin_debit_rate?.minimum}% or higher.`,
          );
      }),

    tiered_pin_debit_per_transaction: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when(["pricing_model", "tiered_pin_debit_network_fee"], (pricing_model, tiered_pin_debit_network_fee, schema) => {
        if (pricing_model !== "tiered" || tiered_pin_debit_network_fee !== "yes") return schema.notRequired();

        return schema
          .required("PIN-based Debit Per Txn fee is required")
          .min(
            pricingDefaults?.tiered?.tiered_pin_debit_per_transaction?.minimum,
            `Please enter a fee of $${pricingDefaults?.tiered?.tiered_pin_debit_per_transaction?.minimum} or higher.`,
          );
      }),

    ebt: Yup.string()
      .oneOf(["yes", "no"], "EBT is required")
      .required("EBT is required"),
    fnf_number: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .nullable()
      .when("ebt", {
        is: (ebt) => ebt === "yes",
        then: (schema) => schema.required("* FNF Number is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

    // default fees
    ...getAllDefaultFeeRules(pricingDefaults),

    ebt_transactions: Yup.number().when(
      ["ebt", "pricing_model"],
      (ebt, pricing_model, schema) => {
        if (ebt != "yes") return schema.notRequired();

        const fee = pricingDefaults?.[pricing_model]?.ebt_transactions;

        if (typeof fee?.minimum === "number") {
          schema = schema.min(
            fee.minimum,
            `Please enter a fee of $${fee.minimum.toFixed(2)} or higher.`,
          );
        }

        return schema.required("EBT Transactions is required");
      },
    ),
    // NOTE: Re-enable this when we have a way to add multiple reprogrammable hardware
    // reprogram_hardware: Yup.boolean().required("Reprogram Hardware is required"),
    // serial_number: Yup.array().when("reprogram_hardware", {
    //   is: true,
    //   then: Yup.array()
    //     .of(reprogrammedHardwareSchema)
    //     .min(1, "At least one reprogrammed hardware is required"),
    //   otherwise: Yup.array(),
    // }),

    // use_existing_gateway: Yup.boolean().required(
    //   "* Use Existing Gateway is required",
    // ),
    // existing_gateway: Yup.string().when("use_existing_gateway", {
    //   is: true,
    //   then: Yup.string().required("* Existing Gateway is required"),
    // }),

    var_only: Yup.object()
      .nullable()
      .shape({
        type: Yup.string()
          .oneOf(["terminal", "gateway"], "Invalid VAR Only type")
          .nullable()
          .notRequired(), // use notRequired instead of optional
        model: Yup.string()
          .when("type", {
            is: "gateway",
            then: (schema) => schema.required("* Gateway name is required"),
          })
          .when("type", {
            is: "terminal",
            then: (schema) => schema.required("* Model is required"),
          }),
        serial: Yup.string().when("type", {
          is: "terminal",
          then: (schema) => schema.required("Serial number is required"),
          otherwise: (schema) => schema.nullable().notRequired(),
        }),
      }),

    // Kurv Pricing validation schema
    kurv_pricing_swiped_rate: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when("pricing_model", {
        is: "kurv_pricing",
        then: (schema) =>
          schema
            .required("Kurv Swiped Rate is required")
            .min(
              pricingDefaults?.kurv_pricing?.kurv_pricing_swiped_rate?.minimum,
              `Please enter a rate of ${pricingDefaults?.kurv_pricing?.kurv_pricing_swiped_rate?.minimum}% or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
    kurv_pricing_swiped_txn_fee: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when("pricing_model", {
        is: "kurv_pricing",
        then: (schema) =>
          schema
            .required("Kurv Swiped Txn Fee is required")
            .min(
              pricingDefaults?.kurv_pricing?.kurv_pricing_swiped_txn_fee
                ?.minimum,
              `Please enter a fee of $${pricingDefaults?.kurv_pricing?.kurv_pricing_swiped_txn_fee?.minimum} or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
    kurv_pricing_keyed_rate: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when("pricing_model", {
        is: "kurv_pricing",
        then: (schema) =>
          schema
            .required("Kurv Keyed Rate is required")
            .min(
              pricingDefaults?.kurv_pricing?.kurv_pricing_keyed_rate?.minimum,
              `Please enter a rate of ${pricingDefaults?.kurv_pricing?.kurv_pricing_keyed_rate?.minimum}% or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
    kurv_pricing_keyed_txn_fee: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when("pricing_model", {
        is: "kurv_pricing",
        then: (schema) =>
          schema
            .required("Kurv Keyed Txn Fee is required")
            .min(
              pricingDefaults?.kurv_pricing?.kurv_pricing_keyed_txn_fee
                ?.minimum,
              `Please enter a fee of $${pricingDefaults?.kurv_pricing?.kurv_pricing_keyed_txn_fee?.minimum} or higher.`,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
    kurv_pricing_amex_toggle: Yup.string()
      .oneOf(["yes", "no"], "Must be either 'yes' or 'no'")
      .when("pricing_model", {
        is: "kurv_pricing",
        then: (schema) =>
          schema
            .required("Kurv Amex Toggle is required")
            .oneOf(["yes", "no"], "Must be either 'yes' or 'no'"),
        otherwise: (schema) => schema.notRequired(),
      }),
    kurv_pricing_amex_rate: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when(["pricing_model", "kurv_pricing_amex_toggle"], (pricing_model, kurv_pricing_amex_toggle, schema) => {
        if (pricing_model !== "kurv_pricing" || kurv_pricing_amex_toggle !== "yes") return schema.notRequired();

        return schema
          .required("Kurv Amex Rate is required")
          .min(
            pricingDefaults?.kurv_pricing?.kurv_pricing_amex_rate?.minimum,
            `Please enter a rate of ${pricingDefaults?.kurv_pricing?.kurv_pricing_amex_rate?.minimum}% or higher.`,
          );
      }),
    kurv_pricing_amex_txn_fee: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when(["pricing_model", "kurv_pricing_amex_toggle"], (pricing_model, kurv_pricing_amex_toggle, schema) => {
        if (pricing_model !== "kurv_pricing" || kurv_pricing_amex_toggle !== "yes") return schema.notRequired();

        return schema
          .required("Kurv Amex Txn Fee is required")
          .min(
            pricingDefaults?.kurv_pricing?.kurv_pricing_amex_txn_fee?.minimum,
            `Please enter a fee of $${pricingDefaults?.kurv_pricing?.kurv_pricing_amex_txn_fee?.minimum} or higher.`,
          );
      }),
    kurv_pricing_pin_debit_network_fee: Yup.string()
      .oneOf(["yes", "no"], "Must be either 'yes' or 'no'")
      .when("pricing_model", {
        is: "kurv_pricing",
        then: (schema) =>
          schema
            .required("Kurv PIN Debit Network Fee is required")
            .oneOf(["yes", "no"], "Must be either 'yes' or 'no'"),
        otherwise: (schema) => schema.notRequired(),
      }),
    kurv_pricing_pin_debit_rate: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when(["pricing_model", "kurv_pricing_pin_debit_network_fee"], (pricing_model, kurv_pricing_pin_debit_network_fee, schema) => {
        if (pricing_model !== "kurv_pricing" || kurv_pricing_pin_debit_network_fee !== "yes") return schema.notRequired();

        return schema
          .required("Kurv PIN Debit Rate is required")
          .min(
            pricingDefaults?.kurv_pricing?.kurv_pricing_pin_debit_rate
              ?.minimum,
            `Please enter a rate of ${pricingDefaults?.kurv_pricing?.kurv_pricing_pin_debit_rate?.minimum}% or higher.`,
          );
      }),
    kurv_pricing_pin_debit_per_transaction: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .when(["pricing_model", "kurv_pricing_pin_debit_network_fee"], (pricing_model, kurv_pricing_pin_debit_network_fee, schema) => {
        if (pricing_model !== "kurv_pricing" || kurv_pricing_pin_debit_network_fee !== "yes") return schema.notRequired();

        return schema
          .required("Kurv PIN Debit Per Txn fee is required")
          .min(
            pricingDefaults?.kurv_pricing
              ?.kurv_pricing_pin_debit_per_transaction?.minimum,
            `Please enter a fee of $${pricingDefaults?.kurv_pricing?.kurv_pricing_pin_debit_per_transaction?.minimum} or higher.`,
          );
      }),
    ...(requirePaylianceIndustry ? paylianceIndustryPricingField : {}),
  });

export const createPricingInfoSchema = (pricingDefaults, options) => {
  const standard = buildPricingInfoSchema(pricingDefaults, options);
  const skippedMinimums = buildPricingInfoSchema(
    stripMinimums(pricingDefaults),
    options,
  );

  return Yup.object().shape(
    Object.fromEntries(
      Object.entries(standard.fields).map(([name, schema]) => [
        name,
        Yup.lazy((value, context) =>
          shouldSkipPricingMinimums(context?.parent)
            ? skippedMinimums.fields[name]
            : schema,
        ),
      ]),
    ),
  );
};

export const pricingInfoSchema = createPricingInfoSchema();

export const detailsInfoSchema = Yup.object().shape({
  who_to_deploy_equipment: Yup.string()
    .oneOf(["agent", "processor"], "* Invalid who deploys equipment")
    .required("* Who deploys equipment is required"),
  who_to_pay_for_equipment: Yup.string()
    .oneOf(["agent", "merchant"], "* Invalid who pays for equipment")
    .required("* Who pays for equipment is required"),
  who_to_deploy_gateway: Yup.string()
    .oneOf(["agent", "processor"], "* Invalid who deploy gateway")
    .required("* Who deploy gateway is required"),
  billing_sequence: Yup.string()
    .oneOf(["weekly", "daily"], "* Invalid billing sequence")
    .required("* Billing sequence is required"),
  cover_cancellation: Yup.string()
    .oneOf(["yes", "no"], "* Invalid cover cancellation fees value")
    .required("* Cover cancellation fees is required"),
  reimbursement_maximum: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .nullable()
    .when("cover_cancellation", {
      is: (cover_cancellation) => cover_cancellation === "yes",
      then: (schema) => schema.required("* Reimbursement maximum is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  expedited_funding: Yup.string()
    .oneOf(["yes", "no"], "* Invalid expedited funding value")
    .required("* Expedited funding is required"),
  batch_time: Yup.string()
    .matches(
      /^(0?[1-9]|1[0-2]):[0-5][0-9] (AM|PM)$/i,
      "* Invalid time format (HH:MM AM/PM)",
    )
    .required("* Batch time is required"),
  equipment_address: Yup.string()
    .oneOf(
      ["legal_address", "home_address", "dba_address", "agent_address"],
      "* Invalid ship equipment to",
    )
    .required("* Ship equipment to is required"),
  invoice_address: Yup.string()
    .oneOf(
      ["legal_address", "home_address", "dba_address", "agent_address"],
      "* Invalid send invoice to",
    )
    .required("* Send invoice to is required"),
  special_instructions: Yup.string(),
});

export const processorInfoSchema = Yup.object().shape({
  processors: Yup.array()
    .required("At least one processor is required")
    .min(1, "At least one processor is required")
    .max(2, "At most 2 processors are allowed")
    .test(
      "one-per-type",
      "Only one Credit Card processor and one ACH processor can be selected",
      (value = []) => {
        const achCount = value.filter(isAchProcessorId).length;
        return achCount <= 1 && value.length - achCount <= 1;
      },
    )
    .test(
      "ach-pricing-needs-ach-processor",
      "An ACH processor must be selected when ACH pricing is configured",
      function (value = []) {
        const on =
          this.parent?.pricing_model === "ach" ||
          this.parent?.add_ach === "yes";
        return !on || hasAchProcessor(value);
      },
    )
    .test(
      "cc-pricing-needs-cc-processor",
      "A credit card processor must be selected when credit card pricing is configured",
      function (value = []) {
        const m = this.parent?.pricing_model;
        return !(m && m !== "ach") || hasCcProcessor(value);
      },
    ),
});

// Signers Schema
const signerSchema = Yup.object().shape({
  signer_title: Yup.string().nullable().required("* Title is required"),
  signer_first_name: Yup.string()
    .required("* First Name is required")
    .matches(/^[a-zA-Z]+$/, "* First Name must contain only letters"),
  signer_last_name: Yup.string()
    .required("* Last Name is required")
    .matches(/^[a-zA-Z]+$/, "* Last Name must contain only letters"),
  signer_email: Yup.string()
    .email("Invalid email")
    .required("* Email is required"),
  signer_phone: Yup.string()
    .required("* Phone is required")
    .matches(phoneRegex, "* Enter a valid phone"),
  signer_address: Yup.string().required("* Address is required"),
  signer_city: Yup.string().required("* City is required"),
  signer_state: Yup.string().required("* State is required"),
  signer_zip: Yup.string()
    .required("* ZIP is required")
    .matches(/^\d{5}$/, "* ZIP must be 5 digits"),
  signer_country: Yup.string().when("signer_document_type", {
    is: (val) => val === "PASSPORT",
    then: Yup.string().required("* Country is required"),
    otherwise: Yup.string().nullable(),
  }),
  signer_social_security: Yup.string()
    .required("* Social Security is required")
    .matches(SSNRegex, "* Enter a valid SSN"),
  signer_drivers_license_number: Yup.string().required(
    "* Driver's License Number is required",
  ),
  signer_drivers_license_state: Yup.string().when("signer_document_type", {
    is: (val) => val === "STATE_ID" || val === "LOCAL_STATE_ID",
    then: Yup.string().required("* State is required"),
    otherwise: Yup.string().nullable(),
  }),
  signer_date_of_birth: Yup.string()
    .required("* Date of Birth is required")
    .matches(
      /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/,
      "* Date must be in MM/DD/YYYY format",
    )
    .test(
      "age",
      "* Date of Birth must be between 18 and 120 years old",
      function (value) {
        if (!value) return true;
        const [month, day, year] = value.split("/").map(Number);
        if (
          !month ||
          !day ||
          !year ||
          month < 1 ||
          month > 12 ||
          day < 1 ||
          day > 31 ||
          year < 1000
        ) {
          return false;
        }
        const birthDate = new Date(year, month - 1, day);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (
          monthDiff < 0 ||
          (monthDiff === 0 && today.getDate() < birthDate.getDate())
        ) {
          age--;
        }
        return age >= 18 && age <= 120;
      },
    ),
  signer_document_type: Yup.string().required("* Document Type is required"),
  signer_document_country: Yup.string().when("signer_document_type", {
    is: (val) => val === "PASSPORT",
    then: Yup.string().required("* Document Country is required"),
    otherwise: Yup.string().nullable(),
  }),
  signer_ownership_percentage: Yup.string()
    .max(100, "* Ownership Percentage must be less than 100%")
    .required("* Ownership Percentage is required"),
  signer_residence_years: Yup.number()
    .nullable()
    .min(0, "Years cannot be negative")
    .max(99, "Years must be less than 99")
    .test(
      "total-residence",
      "At least 1 month of residence is required",
      function (value) {
        const { signer_residence_months } = this.parent;
        return value > 0 || signer_residence_months > 0;
      },
    ),
  signer_residence_months: Yup.number()
    .nullable()
    .min(0, "Months cannot be negative")
    .max(11, "Months must be less than 12")
    .test(
      "total-residence",
      "At least 1 month of residence is required",
      function (value) {
        const { signer_residence_years } = this.parent;
        return value > 0 || signer_residence_years > 0;
      },
    ),
});

export const personalInformationSchema = Yup.object().shape({
  title: Yup.string().nullable().required("* Title is required"),
  personal_same_as_dba_legal: Yup.string()
    .nullable()
    .oneOf(["dba", "legal", "no", "business"], "Invalid selection")
    .required("* Selection is required"),
  personal_first_name: Yup.string()
    .nullable()
    .required("* First Name is required"),
  personal_last_name: Yup.string()
    .nullable()
    .required("* Last Name is required"),
  personal_phone: Yup.string()
    .nullable()
    .when("personal_same_as_dba_legal", {
      is: (val) => val === "no",
      then: Yup.string()
        .nullable()
        .matches(phoneRegex, "* Enter a valid phone")
        .required("* Phone is required"),
      otherwise: Yup.string().nullable(),
    }),
  personal_email: Yup.string()
    .email("Invalid email")
    .nullable()
    .required("* Email is required"),
  field_personalstreet: Yup.string()
    .nullable()
    .required("* Street Address is required"),
  field_personalcity: Yup.string().nullable().required("* City is required"),
  field_personalstate: Yup.string().nullable().required("* State is required"),
  field_personalzip: Yup.string().nullable().required("* ZIP is required"),
  field_residenceyears: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value,
    )
    .nullable()
    .min(0, "Years cannot be negative")
    .max(99, "Years must be less than 99")
    .test(
      "total-residence",
      "At least 1 month of residence is required",
      function (value) {
        const { field_residencemonths } = this.parent;
        return value > 0 || field_residencemonths > 0;
      },
    ),
  field_residencemonths: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value,
    )
    .nullable()
    .min(0, "Months cannot be negative")
    .max(11, "Months must be less than 12")
    .test(
      "total-residence",
      "At least 1 month of residence is required",
      function (value) {
        const { field_residenceyears } = this.parent;
        return value > 0 || field_residenceyears > 0;
      },
    ),
  field_documenttype: Yup.string()
    .nullable()
    .required("* Document Type is required"),
  field_documentnumber: Yup.string()
    .nullable()
    .required("* Document Number is required"),
  document_country: Yup.string()
    .nullable()
    .when("field_documenttype", {
      is: (val) => val === "PASSPORT",
      then: Yup.string().nullable().required("* Document Country is required"),
      otherwise: Yup.string().nullable(),
    }),
  field_driverstate: Yup.string()
    .nullable()
    .when("field_documenttype", {
      is: (val) => val === "STATE_ID" || val === "LOCAL_STATE_ID",
      then: Yup.string().nullable().required("* State is required"),
      otherwise: Yup.string().nullable(),
    }),
  field_ownershippercentage: Yup.mixed()
    .nullable()
    .transform((value) => {
      // Convert empty strings to null
      if (value === "") return null;
      // Return the value as is (could be a string representing a number)
      return value;
    })
    .required("* Ownership Percentage is required")
    .test(
      "ownership-total",
      "Total ownership percentage (including additional owners) cannot exceed 100%",
      function (value) {
        // value is the main owner's ownership percentage
        // this.options.context contains the full form values if context is passed to validation
        // but in Formik, we can access siblings via this.parent
        const mainOwner = Number(value) || 0;
        const additionalOwners = this.parent.additional_owners || [];
        let additionalTotal = 0;
        for (const owner of additionalOwners) {
          // Try to parse as float, fallback to 0 if not a number
          let perc = 0;
          if (
            owner &&
            owner.signer_ownership_percentage !== undefined &&
            owner.signer_ownership_percentage !== null &&
            owner.signer_ownership_percentage !== ""
          ) {
            perc = parseFloat(owner.signer_ownership_percentage);
            if (isNaN(perc)) perc = 0;
          }
          additionalTotal += perc;
        }
        return mainOwner + additionalTotal <= 100;
      },
    ),
  field_socialsecurity: Yup.string()
    .nullable()
    .matches(SSNRegex, "* Enter a valid SSN")
    .required("* Social Security Number is required"),
  field_birthdate: Yup.string()
    .nullable()
    .required("* Date of Birth is required")
    .matches(
      /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/,
      "* Date must be in MM/DD/YYYY format",
    )
    .test(
      "age-range",
      "* Date of Birth must be between 18 and 120 years old",
      function (value) {
        if (!value) return true;
        const [month, day, year] = value.split("/").map(Number);
        if (
          !month ||
          !day ||
          !year ||
          month < 1 ||
          month > 12 ||
          day < 1 ||
          day > 31 ||
          year < 1000
        ) {
          return false;
        }
        const birthDate = new Date(year, month - 1, day);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (
          monthDiff < 0 ||
          (monthDiff === 0 && today.getDate() < birthDate.getDate())
        ) {
          age--;
        }
        return age >= 18 && age <= 120;
      },
    ),
  additional_owners: Yup.array().of(signerSchema),
});

export const businessProfileSchema = Yup.object().shape({
  product_service_description: Yup.string().required(
    "* Product/Service Description is required",
  ),
  prod_advertise_description: Yup.array()
    .of(Yup.string())
    .min(1, "* At least one advertising method is required"),
  is_thirdparty_involved: Yup.string()
    .oneOf(["yes", "no"])
    .required("* Third Party Involvement is required"),
  thirdparty_involved_description: Yup.string().when("is_thirdparty_involved", {
    is: "yes",
    then: Yup.string().required("* Third Party Description is required"),
    otherwise: Yup.string().nullable(),
  }),
  pci_complaint: Yup.string()
    .oneOf(["yes", "no"])
    .required("* PCI Compliance is required"),
  terminated_as_vmdax: Yup.string()
    .oneOf(["yes", "no"])
    .required("* VMDAX Termination is required"),
  prior_data_compromise_investigation: Yup.string()
    .oneOf(["yes", "no"])
    .required("* Prior Data Compromise is required"),
  identified_visa_risk_program: Yup.string()
    .oneOf(["yes", "no"])
    .required("* Visa Risk Program is required"),
  visa_risk_program_desc: Yup.string().when("identified_visa_risk_program", {
    is: "yes",
    then: Yup.string().required("* Visa Risk Program Description is required"),
    otherwise: Yup.string().nullable(),
  }),
  is_multiple_locations: Yup.string()
    .oneOf(["yes", "no"])
    .required("* Multiple Locations is required"),
  multi_merchant: requiredWhenMultipleLocations("* Multi-Merchant is required"),
  multi_merchant_of: requiredWhenMultipleLocations("* Multi-Merchant total is required"),
  additional_location: requiredWhenMultipleLocations("* Additional Location is required"),
  additional_location_of: requiredWhenMultipleLocations("* Additional Location total is required"),
});

export const creditCardProcessingSchema = Yup.object().shape({
  currently_accept_vdmax: Yup.string()
    .nullable()
    .oneOf(["yes", "no"], "Please select an option")
    .required("* Please indicate if you currently accept credit cards"),

  recurring_bill_triel_radio: Yup.string()
    .nullable()
    .oneOf(["yes", "no"], "Please select an option")
    .required("* Please indicate if the business uses recurring billing"),

  ways_accepting_cards: Yup.array()
    .min(1, "* Please select at least one way you accept credit & debit cards")
    .required(
      "* Please select at least one way you accept credit & debit cards",
    ),

  // Sales percentage validation - total should be 100%
  internet: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ||
      originalValue === null ||
      originalValue === undefined
        ? 0
        : value,
    )
    .nullable()
    .integer("Value must be a whole number")
    .min(0, "Value cannot be negative")
    .test("sales-total", "Sales percentages must add up to 100%", function () {
      const values = this.parent;
      const totalSales = calculateSalesTotal(values, [
        "internet",
        "sales_swiped",
        "sales_keyed",
      ]);
      return Math.abs(totalSales - 100) < 0.01; // Allow for small floating point differences
    }),

  sales_swiped: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ||
      originalValue === null ||
      originalValue === undefined
        ? 0
        : value,
    )
    .nullable()
    .integer("Value must be a whole number")
    .min(0, "Value cannot be negative")
    .test(
      "sales-type-total",
      "Sales percentages must add up to 100%",
      function () {
        const values = this.parent;
        const totalSales = calculateSalesTotal(values, [
          "sales_swiped",
          "sales_keyed",
          "internet",
        ]);
        return Math.abs(totalSales - 100) < 0.01; // Allow for small floating point differences
      },
    ),

  sales_keyed: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ||
      originalValue === null ||
      originalValue === undefined
        ? 0
        : value,
    )
    .nullable()
    .integer("Value must be a whole number")
    .min(0, "Value cannot be negative")
    .test(
      "sales-type-total",
      "Sales percentages must add up to 100%",
      function () {
        const values = this.parent;
        const totalSales = calculateSalesTotal(values, [
          "sales_swiped",
          "sales_keyed",
          "internet",
        ]);
        return Math.abs(totalSales - 100) < 0.01; // Allow for small floating point differences
      },
    ),
  // Processing amounts - required
  monthly_max_sales: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value,
    )
    .nullable()
    .required("* Max Monthly Sales is required")
    .min(0, "Value cannot be negative")
    .max(1000000000, "Value cannot be greater than 1,000,000,000")
    .test(
      "max-monthly-gte-avg-sale-high-sale",
      "Max Monthly Sales cannot be less than Average Sale Amount or Highest Sale Amount",
      function (value) {
        const { avg_sale_amount, high_sale_amount } = this.parent;
        if (
          value === undefined ||
          value === null ||
          avg_sale_amount === undefined ||
          avg_sale_amount === null ||
          high_sale_amount === undefined ||
          high_sale_amount === null
        ) {
          return true; // Let other validation handle required
        }
        return value >= avg_sale_amount && value >= high_sale_amount;
      },
    ),

  avg_sale_amount: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value,
    )
    .nullable()
    .required("* Average Sale Amount is required")
    .min(0, "Value cannot be negative")
    .max(1000000000, "Value cannot be greater than 1,000,000,000")
    .test(
      "avg-sale-lt-high-sale",
      "Average Sale Amount must be less than Highest Sale Amount",
      function (value) {
        const { high_sale_amount } = this.parent;
        if (
          value === undefined ||
          value === null ||
          high_sale_amount === undefined ||
          high_sale_amount === null
        ) {
          return true; // Let other validation handle required
        }
        return value < high_sale_amount;
      },
    ),

  high_sale_amount: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value,
    )
    .nullable()
    .required("* Highest Sale Amount is required")
    .min(0, "Value cannot be negative")
    .max(1000000000, "Value cannot be greater than 1,000,000,000")
    .test(
      "highest-sale-gt-avg-sale",
      "Highest sale must be greater than Average Sale Amount",
      function (value) {
        const { avg_sale_amount } = this.parent;
        if (
          value === undefined ||
          value === null ||
          avg_sale_amount === undefined ||
          avg_sale_amount === null
        ) {
          return true; // Defer to other rules
        }
        return value > avg_sale_amount;
      },
    )
    .test(
      "highest-sale-lt-monthly-max",
      "Highest Sale Amount must be less than or equal to Max Monthly Sales",
      function (value) {
        const { monthly_max_sales } = this.parent;
        if (
          value === undefined ||
          value === null ||
          monthly_max_sales === undefined ||
          monthly_max_sales === null
        ) {
          return true; // Defer to other rules
        }
        return value <= monthly_max_sales;
      },
    ),
  bank_routing: Yup.string()
    .required("* Routing Number is required")
    .matches(/^\d{9}$/, "* Routing Number must have 9 digits")
    .test(
      "is-valid-routing",
      "* Routing Number must have 9 digits",
      (value) => {
        if (!value) return true;
        return value.length === 9;
      },
    ),
  bank_account: Yup.string().required("* Account Number is required"),
  bank_name: Yup.string().required("* Bank Name is required"),
});

export const cbdAddendumSchema = Yup.object().shape({
  cbd_addendum_file: Yup.mixed().required("* Product Pricing Spreadsheet is required"),
});

export const vapeAttestationSchema = Yup.object().shape({
  vape_attestation_what_is_your_source_of_marketing: Yup.string().nullable().required("* Source of marketing is required"),
  vape_attestation_what_audience_is_the_product_marketed_to: Yup.string().nullable().required("* Audience is required"),
  vape_attestation_product_1: Yup.string().nullable().required("* Product 1 is required"),
  vape_attestation_product_2: Yup.string().nullable().required("* Product 2 is required"),
  vape_attestation_product_3: Yup.string().nullable().required("* Product 3 is required"),
  vape_attestation_product_4: Yup.string().nullable().required("* Product 4 is required"),
  vape_attestation_product_5: Yup.string().nullable().required("* Product 5 is required"),
  where_are_the_products_manufactured: Yup.string().nullable().required("* Manufacturer details are required"),
  vape_attestation_product_types: Yup.array().of(Yup.object().shape({
    name: Yup.string().nullable().required("* Product Type is required"),
  })).min(1),
  vape_attestation_method_of_age_verification: Yup.string().nullable().required("* Method of Age Verification is required"),
  vape_attestation_other_method_of_age_verification: Yup.string().nullable().when("vape_attestation_method_of_age_verification", {
    is: "Other",
    then: Yup.string().nullable().required("* Other Method of Age Verification is required"),
  }),
});

// Travel Addendum Schema
export const travelAddendumSchema = Yup.object().shape({
  travel_business_activities: Yup.array()
    .min(1, "* At least one business activity is required")
    .required("* Business activities are required"),
  travel_other_business_activity: Yup.string().nullable().when("travel_business_activities", {
    is: (val) => val && val.includes("other"),
    then: Yup.string().nullable().required("* Other business activity details are required"),
    otherwise: Yup.string().nullable(),
  }),
  travel_sales_third_party_packages: Yup.string()
    .nullable()
    .test(
      "travel-gross-turnover-fields",
      "* At least one Gross Annual Turnover as a Travel Agent percentage field is required",
      function () {
        const { travel_sales_third_party_packages, travel_agency_accommodation_only, travel_agency_non_flight_transport, travel_flight_own_merchant, travel_flight_airline_merchant, travel_other_sales_agent } = this.parent;
        const fields = [travel_sales_third_party_packages, travel_agency_accommodation_only, travel_agency_non_flight_transport, travel_flight_own_merchant, travel_flight_airline_merchant, travel_other_sales_agent];
        return fields.some((val) => val !== undefined && val !== null && String(val).trim() !== "");
      },
    ),
  travel_agency_accommodation_only: Yup.string().nullable(),
  travel_agency_non_flight_transport: Yup.string().nullable(),
  travel_flight_own_merchant: Yup.string().nullable(),
  travel_flight_airline_merchant: Yup.string().nullable(),
  travel_other_sales_agent: Yup.string().nullable(),
  travel_total_gross_turnover: Yup.string().nullable().required("* Total Gross Turnover is required"),
  travel_airline_suppliers: Yup.array()
    .required("* At least one airline supplier is required")
    .min(1, "* At least one airline supplier is required")
    .of(Yup.object().shape({
      name: Yup.string().nullable().required("* Supplier Name is required"),
      flight_spend_share: Yup.string().nullable().required("* Flight spend share is required"),
      average_package_cost: Yup.string().nullable().required("* Average package cost is required"),
      country: Yup.string().nullable().required("* Country is required"),
      payment_terms: Yup.string().nullable().required("* Payment terms are required"),
    })),
  travel_total_spend_travel: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .nullable()
    .required("* Total spend on travel is required"),
  travel_hotel_suppliers: Yup.array().of(Yup.object().shape({
    name: Yup.string().nullable().required("* Supplier Name is required"),
    country: Yup.string().nullable().required("* Country is required"),
    payment_terms: Yup.string().nullable().required("* Payment terms are required"),
    hotel_spend_share: Yup.string().nullable().required("* Hotel spend share is required"),
    average_package_cost: Yup.string().nullable().required("* Avg package cost is required"),
  })),
  travel_total_spend_other: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .nullable()
    .required("* Total spend on other is required"),
  travel_total_spend_accommodation: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .nullable()
    .required("* Total spend on accommodation is required"),
  travel_local_services: Yup.array().of(Yup.object().shape({
    name: Yup.string().nullable().required("* Provider Name is required"),
    country: Yup.string().nullable().required("* Country is required"),
    payment_terms: Yup.string().nullable().required("* Payment terms are required"),
    service_spend_share: Yup.string().nullable().required("* Service spend share is required"),
    average_package_cost: Yup.string().nullable().required("* Avg package cost is required"),
  })),
  travel_countries: Yup.array().of(Yup.object().shape({
    name: Yup.string().nullable().required("* Country Name is required"),
    trade_share: Yup.string().nullable().required("* Trade share is required"),
  })).min(1, "* At least one country is required"),
  ...(() => {
    const optionKeys = ["pct_annual_sales", "max_monthly_card_sales", "min_monthly_card_sales", "monthly_total_sales"];
    const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
    const schema = {};
    optionKeys.forEach((optionKey) => {
      months.forEach((month) => {
        schema[`travel_seasonality_${optionKey}_${month}`] = Yup.string()
          .nullable()
          .required("* This field is required");
      });
    });
    return schema;
  })(),
  travel_average_deposit_percentage: Yup.string().nullable().required("* Average deposit is required"),
  travel_average_time_deposit_travel: Yup.string().nullable().required("* Months between deposit and balance is required"),
  travel_average_time_balance_travel: Yup.string().nullable().required("* Months between balance and delivery is required"),
  ...(() => {
    const optionKeys = ["1_7_days", "8_14_days", "15_30_days", "31_60_days", "61_90_days", "91_120_days", "121_150_days", "151_180_days", "over_180_days"];
    const schema = {};
    optionKeys.forEach((optionKey) => {
      schema[`travel_delivery_${optionKey}`] = Yup.number()
        .transform((value, originalValue) => (originalValue === "" ? 0 : value))
        .nullable()
        .required("* This field is required");
    });
    return schema;
  })(),
});

// Complete Form Schema
export const formSchema = Yup.object().shape({
  ...businessInformationSchema.fields,
  // commented out because it's not needed for the application form,its all validated at create app form
  // ...contactInfoSchema.fields,
  // ...pricingInfoSchema.fields,
  // ...detailsInfoSchema.fields,
  ...processorInfoSchema.fields,
  ...personalInformationSchema.fields,
  ...businessProfileSchema.fields,
  // These four are collected on the Business Profile step, which is where the
  // inputs live. Review & Submit renders the business profile read-only, so a
  // failure here has no field to attach to: scrollToError finds no element and
  // submit returns silently. They stay required on step 2 and nullable here.
  multi_merchant: Yup.string().nullable(),
  multi_merchant_of: Yup.string().nullable(),
  additional_location: Yup.string().nullable(),
  additional_location_of: Yup.string().nullable(),
  ...creditCardProcessingSchema.fields,
  signers: Yup.array().of(signerSchema),
});

// template form schema
export const templateFormSchema = Yup.object().shape({
  name: Yup.string().trim().required("* Template name is required"),
  mcc_id: Yup.string().nullable(),
  ems_office_id: Yup.string().nullable(),
  north_template_id: Yup.string().nullable(),
  is_editable: Yup.string()
    .oneOf(["yes", "no"], "Must be yes or no")
    .default("yes"),
});

// additional app info schema
export const additionalAppInfoSchema = Yup.object().shape({
  ems_office_id: Yup.string().nullable(),
});
